/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {FinancialType} from './enums/financial.type';
import * as moment from 'moment';
import {CorporateEntity} from './corporate.entity';
import { CustomDocuments } from './custom.documents';
import { StringBuilder } from 'typescript-string-operations';
import { FormType } from './enums/form.type';

export class CheckList {
  get isValid(): boolean {
    const validGeneral = this.validatePurchaseAgreement.length === 0;
    const validEntityBasis = this.ValidateEntityBasis.length === 0;
    const validRoV = this.validateReasonOfValuation.length === 0;
    let validEntity = true;

    if (this.ENTITY_CHECK_LIST) {
      for (const e of this.ENTITY_CHECK_LIST) {
        if (e.validateTaxReturns.length !== 0) {
          validEntity = false;
          break;
        }
      }
    }

    return validGeneral && validRoV && validEntityBasis && validEntity;
  }

  get ValidateEntityBasis(): string[] {
    const errors = [];
    const eDetails: CheckListEntityDetails[] = this.ENTITY_CHECK_LIST.filter(details => details.BASIS_OVERRIDE !==  true);

    if (eDetails.length > 1) {
      const firstDetails: CheckListEntityDetails = eDetails[0];

      if (firstDetails.FINANCIALS) {
        const firstDetailsBasis = firstDetails.FINANCIALS.sort((a, b) => moment(a.DATE).year() - moment(b.DATE).year()).map(fin => fin.IS_BASIS);
        const subDetails: CheckListEntityDetails[] = eDetails.filter(details => details.ID !== firstDetails.ID);

        if (firstDetailsBasis) {
          for (const f of subDetails) {
            if (f.FINANCIALS) {
              const subEntityBasis = f.FINANCIALS.sort((a, b) => moment(a.DATE).year() - moment(b.DATE).year()).map(fin => fin.IS_BASIS);
              if (!this.compareBasis(firstDetailsBasis, subEntityBasis)) {
                errors.push(firstDetails.BS_TOGGLE || f.BS_TOGGLE ?
                  'Income Statements Basis for all Entities must match' : 'Basis for all Entities must match');
                break;
              }
            } else {
              errors.push(`${f.ENTITY.NAME} Basis not selected`);
            }
          }
        } else {
          errors.push(`${firstDetails.ENTITY.NAME} Basis not selected`);
        }

        if (firstDetails){
          for (const sub of subDetails) {

            if(firstDetails.ENTITY_CHECK_DATE !== sub.ENTITY_CHECK_DATE){
              errors.push('Interim Date for all Entities must match');
                break;
            }
          }
        }

        if (firstDetails.BS_TOGGLE) {
          const primaryEntityBSBasis = firstDetails.FINANCIALS.sort((a, b) => moment(a.DATE).year() - moment(b.DATE).year()).map(fin => fin.BS_BASIS);

          if (primaryEntityBSBasis) {
            for (const f of subDetails) {
              if (f.BS_TOGGLE) {
                if (!f.BASIS_OVERRIDE) {
                  if (f.FINANCIALS) {
                    const subEntityBSBasis = f.FINANCIALS.sort((a, b) => moment(a.DATE).year() - moment(b.DATE).year()).map(fin => fin.BS_BASIS);
                    if (!this.compareBasis(primaryEntityBSBasis, subEntityBSBasis)) {
                      errors.push('Balance Sheet Basis for all Entities must match ');
                      break;
                    }
                  } else {
                    errors.push(`${f.ENTITY.NAME} Balance Sheet Basis not selected`);
                  }
                }
              }
            }
          }
        }
      }
    }
    return errors;
  }

  get validateFinancialSetup(): boolean {
    let isSetup = true;

    if (this.ENTITY_CHECK_LIST.length > 1) {
      for (const e of this.ENTITY_CHECK_LIST) {
        if (!e.FINANCIALS || e.FINANCIALS.length === 0)
          isSetup = false;
      }
    } else {
      isSetup = false;
    }

    return isSetup;
  }

  get validatePurchaseAgreement(): string[] {
    const errors = [];

    if (!this.P_PA && !this.P_LOIP && !this.P_NA) {
      errors.push('Purchase Agreement not Chosen');
      return errors;
    }

    if (!this.PURCHASE_AGREEMENT_TYPE) {
      errors.push('Purchase Agreement Type not Chosen');
      return errors;
    }

    return errors;
  }

  get validateReasonOfValuation(): string[] {
    const errors = [];

    if (!this.VALUATION_REASON_TYPE) {
      errors.push('Reason of Valuation not Chosen');
      return errors;
    }

    return errors;
  }

  ENTITY_CHECK_LIST: CheckListEntityDetails[];

  P_PA: boolean;
  // P_PA_TYPE: PurchaseAgreementType;
  P_LOIP: boolean;
  // P_LOIP_TYPE: PurchaseAgreementType;
  P_NA: boolean;
  // P_NA_TYPE: PurchaseAgreementType;

  SBA_PURPOSE: boolean;
  PURCHASE_AGREEMENT_TYPE: PurchaseAgreementType;
  VALUATION_REASON_TYPE: ReasonOfValuationType;

  EXPLANATION: string;

  RE_OWNED: boolean;
  RE_OWNED_APPRAISAL: boolean;
  RE_OWNED_APPRAISAL_CONTACT: boolean;

  RE_CONTACT_NAME: string;
  RE_CONTACT_PHONE: string;
  RE_CONTACT_EMAIL: string;
  RE_CONTACT_COMPANY: string;

  FIN_FORECAST: boolean;
  CWU: boolean;
  CL_LOIF: boolean;
  LOIF: boolean;
  ORDER_FORM: boolean;
  BVQ_FORM: boolean;
  FDD: boolean;
  BUSINESS_PLAN: boolean;
  //NA Reasons
  FIN_FORECAST_NA_REASON: string;
  CWU_NA_REASON: string
  CL_LOIF_NA_REASON: string;
  LOIF_NA_REASON: string;
  ORDER_FORM_NA_REASON: string;
  BVQ_FORM_NA_REASON: string;
  FDD_NA_REASON: string;
  BUSINESS_PLAN_NA_REASON: string;

  CUSTOM_DOCUMENTS: CustomDocuments[];

  static parse(json: any): CheckList {
    if (json) {
      const r = new CheckList();

      r.ENTITY_CHECK_LIST = CheckListEntityDetails.parseArray(json.ENTITY_CHECK_LIST);

      r.P_PA = json.P_PA;
      // r.P_PA_TYPE = PurchaseAgreementType[<string>json.P_PA_TYPE];

      r.P_LOIP = json.P_LOIP;
      // r.P_LOIP_TYPE = PurchaseAgreementType[<string>json.P_LOIP_TYPE];

      r.P_NA = json.P_NA;
      // r.P_NA_TYPE = PurchaseAgreementType[<string>json.P_NA_TYPE];

      // r.REASON_OF_VALUATION = json.REASON_OF_VALUATION;
      r.SBA_PURPOSE = json.SBA_PURPOSE;

      r.PURCHASE_AGREEMENT_TYPE = PurchaseAgreementType[<string>json.PURCHASE_AGREEMENT_TYPE];
      r.VALUATION_REASON_TYPE = ReasonOfValuationType[<string>json.VALUATION_REASON_TYPE];

      r.EXPLANATION = json.EXPLANATION;

      r.RE_OWNED = json.RE_OWNED;
      r.RE_OWNED_APPRAISAL = json.RE_OWNED_APPRAISAL;
      r.RE_OWNED_APPRAISAL_CONTACT = json.RE_OWNED_APPRAISAL_CONTACT;

      r.FIN_FORECAST = json.FIN_FORECAST;
      r.CWU = json.CWU;
      r.CL_LOIF = json.CL_LOIF;
      r.LOIF = json.LOIF;
      r.ORDER_FORM = json.ORDER_FORM;
      r.BVQ_FORM = json.BVQ_FORM;
      r.BUSINESS_PLAN = json.BUSINESS_PLAN;
      r.FDD = json.FDD;

      //NA Reasons
      r.FIN_FORECAST_NA_REASON = json.FIN_FORECAST_NA_REASON;
      r.CWU_NA_REASON = json.CWU_NA_REASON;
      r.CL_LOIF_NA_REASON = json.CL_LOIF_NA_REASON;
      r.LOIF_NA_REASON = json.LOIF_NA_REASON;
      r.ORDER_FORM_NA_REASON = json.ORDER_FORM_NA_REASON;
      r.BVQ_FORM_NA_REASON = json.BVQ_FORM_NA_REASON;
      r.FDD_NA_REASON = json.FDD_NA_REASON;
      r.BUSINESS_PLAN_NA_REASON = json.BUSINESS_PLAN_NA_REASON;

      r.RE_CONTACT_NAME = json.RE_CONTACT_NAME;
      r.RE_CONTACT_PHONE = json.RE_CONTACT_PHONE;
      r.RE_CONTACT_EMAIL = json.RE_CONTACT_EMAIL;
      r.RE_CONTACT_COMPANY = json.RE_CONTACT_COMPANY;

      r.CUSTOM_DOCUMENTS = CustomDocuments.parseArray(json.CUSTOM_DOCUMENTS);

      return r;
    } else return null;
  }

  private compareBasis(primaryEntity: string[], subEntity: string[]): boolean {
    if (primaryEntity.length !== subEntity.length)
      return false;

    for (let i = 0; i < primaryEntity.length; i++) {

      if (!primaryEntity[i] || !subEntity[i])
        return false;

      if (primaryEntity[i].toLowerCase() !== subEntity[i].toLowerCase()) {
        return false;
      }
    }

    return true;
  }

  get isPrimaryEntityHaveDate(): boolean {
    if (this.ENTITY_CHECK_LIST != null) {
      for (const details of this.ENTITY_CHECK_LIST) {
        if (details.ENTITY.IS_DEFAULT) {
          if (details.ENTITY_CHECK_DATE != null) {
            return true;
          }
        }
      }
    }

    return false;
  }
}

export class CheckListEntityDetails {
  ID: string;
  ENTITY: CorporateEntity;
  ENTITY_CHECK_DATE: string;
  BS_ENTITY_CHECK_DATE: string;
  FINANCIALS: CheckListEntityFinancials[];
  FINANCIALS_OVERRIDE: boolean;
  FINANCIALS_REASON: string;

  AR: boolean;
  AR_DATE: string;
  AP: boolean;
  AP_DATE: string;
  BS_TOGGLE: boolean;
  BASIS_OVERRIDE: boolean;

  // Helpers
  selected = false;
  same: string;
  bs_same: string;
  isCollapsed = true;
  bsDateValue: Date;

  get isAllowedBasisNotMatch(): boolean {
    for (const f of this.FINANCIALS) {
      if (f.IS_BASIS) {
        if (f.IS_BASIS.toLowerCase() === 'CASH'.toLowerCase())
          return false;
      }
    }

    return true;
  }

  get isAllowedBsBasisNotMatch(): boolean {
    for (const f of this.FINANCIALS) {
      if (f.BS_BASIS) {
        if (f.BS_BASIS.toLowerCase() === 'CASH'.toLowerCase())
          return false;
      }
    }

    return true;
  }

  get validateTaxReturns(): string[] {
    const errors = [];

    if (!this.FINANCIALS) {
      errors.push('Financials Not Setup');
      return errors;
    }

    if (this.FINANCIALS.length === 0) {
      errors.push('Financials Not Setup');
      return errors;
    }

    /*if(this.FINANCIALS){
      if (this.AP != true && this.AR != true) {
        errors.push('AR/AP Date is required');
      }

      if(this.AP){
        if(!this.AP_DATE)
          errors.push('AP Date is required');
      }
      else if(this.AR){
        if(!this.AR_DATE)
         errors.push('AR Date is required');
      }
    }*/


    // Full Year Check
    for (const f of this.FINANCIALS) {

      if (f.TYPE !== FinancialType.NOT_AVAILABLE && f.TYPE !== FinancialType.REQUEST) {
        if (!this.FINANCIALS_OVERRIDE)
          if (f.FULL !== true)
            errors.push(`${f.YEAR} year is not Full`);

        if (!f.IS_BASIS) {
          if (!this.BASIS_OVERRIDE)
            errors.push(this.BS_TOGGLE ? `${f.YEAR} year Income Statements Basis not selected` : `${f.YEAR} year Basis not selected`);
        }

        if(!f.TAX_TYPE){
          errors.push(this.BS_TOGGLE ? `${f.YEAR} year Income Statements Tax Form Type not selected` : `${f.YEAR} year Tax Form Type not selected`);
        }

      }

      if(f.TYPE === FinancialType.REQUEST && this.FINANCIALS.length <= 2){
        errors.push(`${f.YEAR} year financials requested`);
      }

      if (this.BS_TOGGLE) {
        if (f.BS_TYPE !== FinancialType.NOT_AVAILABLE && f.BS_TYPE !== FinancialType.REQUEST) {

          if (!f.BS_BASIS)
            if (!this.BASIS_OVERRIDE)
              errors.push(`${f.YEAR} year Balance Sheet Basis not selected`);

          if(!f.BS_TAX_TYPE)
             errors.push(`${f.YEAR} year Balance Sheet Tax Form Type not selected`);
        }
      }
    }

    // Basis Match Check
    let baseBasis;
    let baseBsBasis;
    for (const f of this.FINANCIALS) {
      if (!this.BASIS_OVERRIDE)
        if (f.IS_BASIS) {
          if (!this.isAllowedBasisNotMatch) {
            if (baseBasis) {
              if (baseBasis.toLowerCase() !== f.IS_BASIS.toLowerCase()) {
                errors.push(this.BS_TOGGLE ? 'Income Statements Basis does not match' : 'Basis does not match');
                break;
              }
            } else baseBasis = f.IS_BASIS;
          }
        }

      if (this.BS_TOGGLE) {
        if (!this.BASIS_OVERRIDE)
          if (f.BS_BASIS) {
            if (!this.isAllowedBsBasisNotMatch) {
              if (baseBsBasis) {
                if (baseBsBasis.toLowerCase() !== f.BS_BASIS.toLowerCase()) {
                  errors.push('Balance Sheet Basis does not match');
                  break;
                }
              } else baseBsBasis = f.BS_BASIS;
            }
          }
      }
    }

    if (this.FINANCIALS_OVERRIDE !== true) {
      // Interim Months Check
      let lastYear: CheckListEntityFinancials;
      for (const f of this.FINANCIALS) {
        if (lastYear) {
          if (moment(f.DATE).isAfter(lastYear.DATE))
            lastYear = f;
        } else lastYear = f;
      }

      const months = moment().diff(moment(lastYear.DATE), 'months', true);
      if (months > 6)
        errors.push(`Data is ${months.toFixed(2)} months old`);

    } else {
      // Check for Text in Override Box
      if (!this.FINANCIALS_REASON) {
        errors.push('Override Reason not Filled');
        return errors;
      }
      if (this.FINANCIALS_REASON.length === 0) {
        errors.push('Override Reason not Filled');
        return errors;
      }
    }

    return errors;
  }

  get getBasisSelected(): boolean {
    let isSelected = true;

    for (const f of this.FINANCIALS) {
      if (!f.IS_BASIS) {
        isSelected = false;
      }
    }

    return isSelected;
  }

  static parse(json: any): CheckListEntityDetails {
    if (json) {
      const r = new CheckListEntityDetails();

      r.ID = json.ID;
      r.ENTITY = CorporateEntity.parse(json.ENTITY);
      r.ENTITY_CHECK_DATE = json.ENTITY_CHECK_DATE;
      r.BS_ENTITY_CHECK_DATE = json.BS_ENTITY_CHECK_DATE;

      if (json.FINANCIALS){
        r.FINANCIALS = CheckListEntityFinancials.parseArray(json.FINANCIALS);

        if(r.FINANCIALS.length > 0){
          r.same = r.FINANCIALS[0].IS_BASIS;
          r.bs_same = r.FINANCIALS[0].BS_BASIS;
        }
      }

      r.FINANCIALS_OVERRIDE = json.FINANCIALS_OVERRIDE;
      r.FINANCIALS_REASON = json.FINANCIALS_REASON;
      r.AR = json.AR;
      r.AP = json.AP;

      if(json.AR_DATE)
        r.AR_DATE = moment(json.AR_DATE).format('MM/DD/YYYY');

      if(json.AP_DATE)
        r.AP_DATE = moment(json.AP_DATE).format('MM/DD/YYYY');

      r.BS_TOGGLE = json.BS_TOGGLE ? json.BS_TOGGLE : false;
      r.BASIS_OVERRIDE = json.BASIS_OVERRIDE ? json.BASIS_OVERRIDE : false;

      //FIXME: [Ronnel] Need to check how this works
      if (json.BS_ENTITY_CHECK_DATE)
        r.bsDateValue = moment(json.ENTITY_CHECK_DATE, 'YYYY-MM-DD').toDate();



      return r;
    } else return null;
  }

  static parseArray(json: any[]): CheckListEntityDetails[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(CheckListEntityDetails.parse(j));

    return arr;
  }

  getCloneFinancials(details: CheckListEntityDetails): CheckListEntityFinancials[] {
    if (this.FINANCIALS != null) {
      if (this.FINANCIALS.length > 0) {
        const arr: CheckListEntityFinancials[] = [];
        for (const fin of this.FINANCIALS) {
          arr.push(fin.getClone(details));
        }
        return arr;
      }
    }
    return null;
  }

  get isDateEqual(): boolean {
    if (this.ENTITY_CHECK_DATE && this.BS_ENTITY_CHECK_DATE) {
      return this.ENTITY_CHECK_DATE.valueOf() === this.BS_ENTITY_CHECK_DATE.valueOf();
    }
    return false;
  }
}

export class CheckListEntityFinancials {
  ID: string;
  YEAR: number;
  DATE: string;
  TYPE: FinancialType;
  TAX_TYPE: FormType;
  IS_BASIS: string;
  FULL: boolean;
  INTERIM: boolean;
  DETAILS: CheckListEntityDetails;
  BS_YEAR: number;
  BS_DATE: string;
  BS_TYPE: FinancialType;
  BS_TAX_TYPE: FormType;
  BS_BASIS: string;
  BS_INTERIM: boolean;

  DATE_TS: number;

  static createDefault(YEAR: number, DATE: string, TYPE: FinancialType,
    BASIS?: string, FULL?: boolean, INTERIM?: boolean): CheckListEntityFinancials {
    const clf = new CheckListEntityFinancials();

    clf.YEAR = YEAR;
    clf.DATE = DATE;
    clf.TYPE = TYPE;
    clf.IS_BASIS = BASIS;
    clf.FULL = FULL;
    clf.INTERIM = INTERIM;

    return clf;
  }

  static createFinancials(field: string, financials: CheckListEntityFinancials, YEAR: number, DATE: string, TYPE?: FinancialType,
    INTERIM?: boolean): CheckListEntityFinancials {

    if (field === 'BS_ENTITY_CHECK_DATE') {

      financials.BS_YEAR = YEAR;
      financials.BS_DATE = DATE;

      if (TYPE)
        financials.BS_TYPE = TYPE;

      financials.BS_INTERIM = INTERIM;


    } else if (field === 'ENTITY_CHECK_DATE') {

      financials.YEAR = YEAR;
      financials.DATE = DATE;

      if (TYPE)
        financials.TYPE = TYPE;

      financials.INTERIM = INTERIM;
    } else {
      financials.YEAR = YEAR;
      financials.DATE = DATE;

      financials.BS_YEAR = YEAR;
      financials.BS_DATE = DATE;

      if (TYPE) {
        financials.TYPE = TYPE;
        financials.BS_TYPE = TYPE;
      }

      financials.INTERIM = INTERIM;
      financials.BS_INTERIM = INTERIM;
    }

    return financials;

  }

  static createIncomStatement(financials: CheckListEntityFinancials, YEAR: number, DATE: string, TYPE?: FinancialType,
    INTERIM?: boolean): CheckListEntityFinancials {
    const clf = new CheckListEntityFinancials();

    clf.BS_YEAR = financials.BS_YEAR;
    clf.BS_DATE = financials.BS_DATE;
    clf.BS_TYPE = financials.BS_TYPE;
    clf.BS_TAX_TYPE = financials.BS_TAX_TYPE;
    clf.BS_BASIS = financials.BS_BASIS;
    clf.BS_INTERIM = financials.BS_INTERIM;

    clf.YEAR = YEAR;
    clf.DATE = DATE;
    clf.TYPE = TYPE ? TYPE : financials.TYPE;
    clf.IS_BASIS = financials.IS_BASIS;
    clf.TAX_TYPE = financials.TAX_TYPE;
    clf.INTERIM = INTERIM;

    clf.FULL = financials.FULL;

    return clf;

  }

  static createDefaultInterim(YEAR: number, DATE: string, TYPE: FinancialType, INTERIM: boolean): CheckListEntityFinancials {
    const clf = new CheckListEntityFinancials();

    clf.YEAR = YEAR;
    clf.DATE = DATE;
    clf.TYPE = TYPE;
    clf.INTERIM = INTERIM;

    return clf;
  }

  static parse(json: any): CheckListEntityFinancials {
    if (json) {
      const r = new CheckListEntityFinancials();

      r.ID = json.ID;
      r.YEAR = Number(json.YEAR);
      // r.DATE = new Date(json.DATE);
      r.DATE = json.DATE;
      r.TYPE = FinancialType[<string>json.TYPE];
      r.TAX_TYPE = FormType[<string>json.TAX_TYPE];
      r.IS_BASIS = json.IS_BASIS;
      r.FULL = json.FULL;
      r.INTERIM = json.INTERIM;
      // r.BS_DATE = new Date(json.BS_DATE);
      r.BS_DATE = json.BS_DATE;
      r.BS_TYPE = FinancialType[<string>json.BS_TYPE];
      r.BS_TAX_TYPE = FormType[<string>json.BS_TAX_TYPE];
      r.BS_BASIS = json.BS_BASIS;

      r.DATE_TS = json.DATE_TS;

      return r;
    } else return null;
  }

  static parseArray(json: any[]): CheckListEntityFinancials[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(CheckListEntityFinancials.parse(j));

    return arr;
  }

  getClone(details: CheckListEntityDetails): CheckListEntityFinancials {
    const x: CheckListEntityFinancials = new CheckListEntityFinancials();

    x.YEAR = this.YEAR;
    x.DATE = this.DATE;
    x.TYPE = this.TYPE;
    x.BS_TYPE = this.BS_TYPE;
    x.INTERIM = this.INTERIM;
    x.DETAILS = details;

    return x;
  }

  isFullDisable(): boolean {
    if (!this.TYPE || this.TYPE === FinancialType.NOT_AVAILABLE || this.TYPE === FinancialType.REQUEST)
      return true;

    return false;
  }

  isBsBasisClear(): boolean {
    if (!this.BS_TYPE || this.BS_TYPE === FinancialType.NOT_AVAILABLE || this.BS_TYPE === FinancialType.REQUEST)
      return true;

    return false;
  }
}

export class CheckListFinancialsSetup {
  DATA: CheckListEntityFinancials[];
  INTERIM: boolean;
  NOT_DEC: boolean;
  FYE_DATE: Date;
  INT_DATE: Date;
}

export enum PurchaseAgreement {
  PA = 'PA',
  LOI = 'LOI',
  NA = 'NA'
}

export enum PurchaseAgreementType {
  STOCK = 'STOCK',
  ASSET = 'ASSET',
  TO_BE_DETERMINED = 'TO_BE_DETERMINED'
}

export enum ReasonOfValuationType {
  SALE_ACQUISITION = 'SALE_ACQUISITION',
  REFINANCE_OF_DEBT = 'REFINANCE_OF_DEBT',
  COLLATERALIZATION = 'COLLATERALIZATION'
}
